import { E as EVENTS } from './events-58bc9098.js';
import { s as srraf } from './srraf.es-487187f3.js';

let loaded$1 = null;

function loadVimeoAPI() {
  if (loaded$1 !== null) return loaded$1

  if (window.Vimeo && window.Vimeo.loaded) {
    loaded$1 = Promise.resolve();
    return loaded$1
  }

  // Otherwise, load API
  loaded$1 = new Promise((resolve) => {
    const tag = document.createElement("script");
    tag.src = "https://player.vimeo.com/api/player.js";
    tag.onload = resolve;
    document.body.appendChild(tag);
  });

  return loaded$1
}

let loaded = null;

function loadYouTubeAPI() {
  // Loading was triggered by a previous call to function
  if (loaded !== null) return loaded

  // API has already loaded
  if (window.YT && window.YT.loaded) {
    loaded = Promise.resolve();
    return loaded
  }

  // Otherwise, load API
  loaded = new Promise((resolve) => {
    window.onYouTubeIframeAPIReady = () => {
      resolve();
    };
    const tag = document.createElement("script");
    tag.src = "https://www.youtube.com/iframe_api";
    document.body.appendChild(tag);
  });

  return loaded
}

// Global arrays to track active players to more easily control.
const youTubePlayerArr = [];
const vimeoPlayerArr = [];
const internalPlayerArr = [];

const addPlayerToTrackingArr = (source, player) => {
  if (source === "youtube") {
    youTubePlayerArr.push(player);
  }

  if (source === "vimeo") {
    vimeoPlayerArr.push(player);
  }

  if (source === "internal") {
    internalPlayerArr.push(player);
  }
};

/**
 * If a video is played, any previously started video will pause
 * If a slide (scroll slider or media lightbox) changes, a playing video will pause
 */
const pausePlayingVideos = () => {
  youTubePlayerArr.forEach((player) => {
    player.pauseVideo();
  });

  vimeoPlayerArr.forEach((player) => {
    player.pause();
  });

  internalPlayerArr.forEach((player) => {
    player.pause();
  });
};

const clearPlayerTrackingArr = () => {
  youTubePlayerArr.length = 0;
  vimeoPlayerArr.length = 0;
  internalPlayerArr.length = 0;
};

/*
  YouTube specific state control
-------------------------------------------------------------------------- */
/**
 * Use YouTube player functions to alter settigns and state if poster clicked
 * @param {HTMLIFrameElement} player result of YT.Player
 *
 */
const clickPosterToPlayYouTube = (player) => {
  // Clicking the play button will play video with sound
  // This should true for mobile, safari, and low power mode
  if (!player) return

  player.unMute();
  player.playVideo();
};

const onYouTubePlayerStateChange = (event) => {
  // -1 is 'unstarted' likely due to API not being fully loaded
  if (event.data === -1) {
    event.target.mute();
    event.target.playVideo();
  }

  youTubePlayerArr.forEach((player) => {
    // Don't pause most recently played video, 1 = playing
    if (player !== event.target && event.data === 1) {
      player.pauseVideo();
    }
  });

  vimeoPlayerArr.forEach((player) => {
    player.pause();
  });

  internalPlayerArr.forEach((player) => {
    player.pause();
  });
};

/*
  Vimeo specific state control
-------------------------------------------------------------------------- */

/**
 * Use Vimeo player functions to alter settings and state if poster clicked
 * @param {HTMLIFrameElement} player result of Vimeo.Player
 *
 */
const clickPosterToPlayVimeo = (player) => {
  // Clicking play button will play video with sound
  // except on mobile, which will be muted or requires click to play in low power mode
  if (!player) return

  player.getPaused().then(function (paused) {
    if (paused) {
      player.play();
    } else {
      player.setMuted(true);
      player.play();
    }
  });
};

/**
 * Create a YouTube video player directly from the API
 * @param {HTMLElement} video video player div that will be swapped out with iframe
 * @param {boolean} hadPoster dictate the presence of poster image over video
 */
const initYouTubeVideo = (video, hadPoster) => {
  const videoId = video.dataset.videoId;

  let autoplay = false;
  if (hadPoster) {
    autoplay = true;
  }

  loadYouTubeAPI().then(() => {
    const player = new window.YT.Player(video, {
      videoId,
      playerVars: {
        enablejsapi: 1,
        cc_load_policy: 0,
        controls: 1,
        iv_load_policy: 3,
        playsinline: 1,
        rel: 0,
        playlist: videoId,
        // eslint-disable-next-line object-shorthand
        autoplay: autoplay,
      },
      events: {
        onReady: () => {
          player.getIframe().tabIndex = "0";
          if (hadPoster) {
            clickPosterToPlayYouTube(player);
          } else {
            player.unMute();
          }

          addPlayerToTrackingArr("youtube", player);
        },
        onStateChange: onYouTubePlayerStateChange,
      },
    });
  });
};

/**
 * Update an existing YouTube iframe to allow for API events and actions
 * @param {HTMLIFrameElement} iframe existing iframe, not originally rendered through the API
 * @param {boolean} hadPoster dictate the presence of poster image over video
 */
const updateIframeYouTubeVideo = (iframe, hadPoster) => {
  loadYouTubeAPI().then(() => {
    const player = new window.YT.Player(iframe, {
      events: {
        onReady: () => {
          player.getIframe().tabIndex = "0";

          if (hadPoster) {
            clickPosterToPlayYouTube(player);
          } else {
            player.unMute();
          }

          addPlayerToTrackingArr("youtube", player);
        },
        onStateChange: onYouTubePlayerStateChange,
      },
    });
  });
};

/**
 * Create a Vimeo video player directly from the API
 * @param {HTMLElement} video video player div that will be swapped out with iframe
 * @param {boolean} hadPoster dictate the presence of poster image over video
 */
const initVimeoVideo = (video, hadPoster) => {
  let autoplay = false;
  if (hadPoster) {
    autoplay = true;
  }

  loadVimeoAPI().then(() => {
    const player = new window.Vimeo.Player(video, {
      id: video.dataset.videoId,
      // eslint-disable-next-line object-shorthand
      autoplay: autoplay,
    });

    player.element.tabIndex = "0";
    if (hadPoster) {
      clickPosterToPlayVimeo(player);
    }

    pausePlayingVideos();
    addPlayerToTrackingArr("vimeo", player);
  });
};

/**
 * Update an existing Vimeo iframe to allow for API events and actions
 * @param {HTMLIFrameElement} iframe existing iframe, not originally rendered through the API
 * @param {boolean} hadPoster dictate the presence of poster image over video
 */
const updateIframeVimeoVideo = (iframe, hadPoster) => {
  loadVimeoAPI().then(() => {
    const player = new window.Vimeo.Player(iframe, {
      muted: true,
    });

    player.element.tabIndex = "0";
    if (hadPoster) {
      clickPosterToPlayVimeo(player);
    }

    addPlayerToTrackingArr("vimeo", player);
  });
};

/**
 * Create new YouTube and Vimeo videos. Initialize YouTube, Vimeo and Shopify videos when triggered.
 * @param {HTMLDivElement} node video container with data to structure triggers with video player created during initialization
 */
const liquidMadeVideoPlayer = (node) => {
  const { showPoster, hasPoster } = node.dataset;
  const playButton = node.querySelector("[js-poster-play-button]");

  if (showPoster && hasPoster) {
    playButton.addEventListener("click", () => {
      node.dataset.videoInitialized = "true";
      _initLiquidMadeVideoPlayer(node, { hadPoster: true });
    });

    playButton.addEventListener("keydown", (event) => {
      if (event.key === "Enter") {
        node.dataset.videoInitialized = "true";
        _initLiquidMadeVideoPlayer(node, { hadPoster: true });
      }
    });
  } else {
    _initLiquidMadeVideoPlayer(node, { hadPoster: false });
  }
};

const _initLiquidMadeVideoPlayer = (node, poster) => {
  const internalVideo = node.querySelector("[data-video-internal]");
  const externalVideo = node.querySelector("[data-video-external]");

  if (externalVideo) {
    const { videoProvider } = externalVideo.dataset;

    if (videoProvider === "youtube") {
      initYouTubeVideo(externalVideo, poster.hadPoster);
    } else if (videoProvider === "vimeo") {
      initVimeoVideo(externalVideo, poster.hadPoster);
    }
  } else if (internalVideo && poster.hadPoster) {
    pausePlayingVideos();
    internalVideo.play();

    // Delay adding player to arr to prevent pre-emptive pause
    setTimeout(() => {
      addPlayerToTrackingArr("internal", internalVideo);
    }, 100);
  }
};

/**
 * When triggered, append and initialize prepared YouTube, Vimeo and Shopify videos.
 * @param {HTMLDivElement} videoContainer container with data to structure triggers and initialization
 * @param {HTMLElement} video iframe (YouTube & Vimeo) or video element (Shopify) to append
 */
const domMadeVideoPlayer = (videoContainer, video) => {
  const { videoType, showPoster } = videoContainer.dataset;

  if (showPoster === "true") {
    const playButton = videoContainer.querySelector("[js-poster-play-button]");

    playButton.addEventListener("click", () => {
      videoContainer.setAttribute("data-video-initialized", "true");
      videoContainer.appendChild(video);

      _initDomMadeVideoPlayer(video, videoType, { hadPoster: true });
    });

    playButton.addEventListener("keydown", (event) => {
      if (event.key === "Enter") {
        videoContainer.setAttribute("data-video-initialized", "true");
        videoContainer.appendChild(video);

        _initDomMadeVideoPlayer(video, videoType, { hadPoster: true });
      }
    });
  } else {
    videoContainer.appendChild(video);
    _initDomMadeVideoPlayer(video, videoType, { hadPoster: false });
  }
};

const _initDomMadeVideoPlayer = (video, videoType, poster) => {
  if (videoType === "youtube") {
    updateIframeYouTubeVideo(video.id, poster.hadPoster);
  } else if (videoType === "vimeo") {
    updateIframeVimeoVideo(video, poster.hadPoster);
  } else {
    if (poster.hadPoster === true) {
      // Only play Shopify video element if poster removed
      pausePlayingVideos();
      addPlayerToTrackingArr("internal", video);
      video.play();
    }
  }
};

const { icons } = window.theme;

function setupMediaLightbox(node) {
  
  if (!node) return;

  // ✅ evita istanze multiple sullo stesso container
if (node._pswpLb) {
  try { node._pswpLb.pswp?.close?.(); } catch (e) {}
  try { node._pswpLb.destroy?.(); } catch (e) {}
  node._pswpLb = null;
  node._pswpActiveLb = null; // ✅ aggiungi
}


  import(flu.chunks.photoswipe).then(({ PhotoSwipeLightbox, PhotoSwipe }) => {
const getVisibleTriggers = () => {
  // ✅ dopo il rebuild, dentro .product-media__items ci sono SOLO le slide della variante
  return Array.from(
    node.querySelectorAll(
      '.product-media__items [js-media-item-wrapper] .lightbox-media'
    )
  );
};


const create = () => {
  const updateVariantCounter = (pswpInstance) => {
    const pswpRoot = pswpInstance?.element;
    if (!pswpRoot) return;

    const counterEl = pswpRoot.querySelector(".pswp__counter");
    if (!counterEl) return;

    const visibleTriggers = Array.from(
      node.querySelectorAll(
        '.product-media__items a.lightbox-media[data-pswp-media-id]'
      )
    );

    const total = visibleTriggers.length;
    if (!total) return;

    const currentEl = pswpInstance?.currSlide?.data?.element;
    const currentId = currentEl?.dataset?.pswpMediaId;

    let currentIndex = 1;

    if (currentId) {
      const idx = visibleTriggers.findIndex(
        (a) => a.dataset.pswpMediaId === currentId
      );
      if (idx >= 0) currentIndex = idx + 1;
    } else {
      currentIndex = Math.min((pswpInstance?.currIndex ?? 0) + 1, total);
    }

    counterEl.textContent = `${currentIndex} / ${total}`;
  };

  const lb = new PhotoSwipeLightbox({
    gallery: node,
    children: "a.lightbox-media",
    dataSource: {
      gallery: node,
      children: "a.lightbox-media",
    },
    showHideAnimationType: "zoom",
    pswpModule: PhotoSwipe,
    mainClass: "pswp--product-lightbox",
    bgOpacity: 1,
    loop: false,
    arrowPrevSVG: icons.leftChevronWithStem,
    arrowNextSVG: icons.rightChevronWithStem,
    closeSVG: icons.close,
    zoomSVG: icons.zoom,
    preloadFirstSlide: true,
  });

  lb.addFilter("itemData", (itemData) => {
    const videoUrl = itemData.element?.dataset?.videoUrl;
    if (videoUrl) itemData.videoUrl = videoUrl;
    return itemData;
  });

  lb.on("contentDeactivate", ({ content }) => {
    if (content.type === "video") pausePlayingVideos();
  });

  lb.on("close", () => clearPlayerTrackingArr());

  // 🔥 aggiorna counter quando apre e quando cambi slide
  lb.on("open", () => updateVariantCounter(lb.pswp));
  lb.on("change", () => updateVariantCounter(lb.pswp));

  lb.init();
  node._pswpActiveLb = lb;

  return lb;
};



    node._pswpLb = create();

    // ✅ reinit: NON serve più destroy/recreate
    // basta chiudere se aperta: al prossimo open, dataSource() rilegge i visibili
if (!node._pswpReinitBound) {
node._pswpReinitBound = (e) => {
  if (!e?.detail?.fromFilter) return;

  // chiudi se aperto
  try { node._pswpLb?.pswp?.close?.(); } catch (err) {}

  // ✅ invalida la cache items così PhotoSwipe rilegge il DOM aggiornato
  if (node._pswpLb?.options?.dataSource) {
    node._pswpLb.options.dataSource.items = null; // oppure: delete node._pswpLb.options.dataSource.items
  }
};


  const productMediaEl = node.closest("product-media");
  if (productMediaEl) {
    productMediaEl.addEventListener("product:media:reinit", node._pswpReinitBound);
  }
}


    // ✅ design mode
    if (window.Shopify?.designMode && !node._pswpDesignBound) {
      node._pswpDesignBound = () => {
        try { node._pswpLb?.pswp?.close?.(); } catch (e) {}
        try { node._pswpLb?.destroy?.(); } catch (e) {}
        node._pswpLb = create();
      };
      node.addEventListener("shopify:section:load", node._pswpDesignBound);
    }
  });
}



function setupModelViewer(node) {
  const modelEls = node.querySelectorAll("model-viewer");

  if (modelEls.length !== 0) {
    initModels();
  }

  function initModels() {
    window.Shopify.loadFeatures([
      {
        name: "model-viewer-ui",
        version: "1.0",
        onLoad: initModelViewers,
      },
    ]);

    window.Shopify.loadFeatures([
      {
        name: "shopify-xr",
        version: "1.0",
        onLoad: initShopifyXr,
      },
    ]);
  }

  function initModelViewers(errors) {
    if (errors) return

    modelEls.forEach((model) => {
      initCustomModelButtons(model);
    });
  }

  function initShopifyXr() {
    if (!window.ShopifyXR) {
      document.addEventListener("shopify_xr_initialized", () => {
        initShopifyXr();
      });
    } else {
      document.querySelectorAll("[id^='ModelJSON-']").forEach((modelJSON) => {
        window.ShopifyXR.addModels(JSON.parse(modelJSON.textContent));
        modelJSON.remove();
      });

      window.ShopifyXR.setupXRElements();
    }
  }

  function initCustomModelButtons(model) {
    const viewer = new window.Shopify.ModelViewerUI(model);

    const parentContainer = model.closest(".product-media__item");
    const poster = parentContainer.querySelector(".model-viewer__poster");
    const closeBtn = parentContainer.querySelector(
      ".model-viewer__close-button",
    );

    poster.addEventListener("click", () => {
      parentContainer.classList.add("model-viewer__active");
      viewer.play();
    });

    poster.addEventListener("keydown", (event) => {
      if (event.key === "Enter") {
        parentContainer.classList.add("model-viewer__active");
        viewer.play();
      }
    });

    closeBtn.addEventListener("click", () => {
      parentContainer.classList.remove("model-viewer__active");
      viewer.pause();
    });

    closeBtn.addEventListener("keydown", (event) => {
      if (event.key === "Enter") {
        parentContainer.classList.remove("model-viewer__active");
        viewer.pause();
      }
    });
  }
}

class ProductMedia extends HTMLElement {
  constructor() {
    super();

    this.selectors = {
      activeThumb: ".product-thumbnails__item-link.active",
      carouselTabIndex: "[js-update-carousel-tabindex]",
      initialMediaContainer: "[data-initial-slide]",
      iframe: "iframe",
      image: ".image",
      mainMediaContainer: ".product-media__items",
      media: ".media",
      mediaItemWrapper: "[js-media-item-wrapper]",
      mobileLoopButton: ".scroll-slider__mobile-loop-nav",
      thumb: "[js-product-thumbnail]",
      thumbById: (id) => `[data-thumbnail-id='${id}']`,
      mediaById: (id) => `[data-slide-index='${id}']`,
      thumbContainer: "[js-product-thumbnails]",
      thumbGradient: ".product-thumbnails__end-cap-gradient",
      video: "video",
      videoPlayerContainer: "[js-video-player-container]",
      videoPoster: ".video-poster-container",
      viewInYourSpace: "[js-in-your-space]",
    };
  }
reindexVisibleMedia() {
  // solo quelle effettivamente nel DOM (dopo rebuild)
  const slides = Array.from(
    this.querySelectorAll(".product-media__items [js-media-item-wrapper]")
  );

  slides.forEach((el, i) => {
    const idx = i + 1;
    el.dataset.slideIndex = String(idx);
    el.dataset.mediaIndex = String(idx);
    // (opzionale) se lo slider usa aria o altro, qui è il posto
  });

  // aggiorna dataset count sul custom element (se qualche script lo usa)
  this.dataset.mediaCount = String(slides.length);
}

  connectedCallback() {
    this.controller = new AbortController();

    this.initActiveMedia();
   // ✅ aggiungi qui
    this.initVariantMediaFilter();
    // Listen for variant change, handle media depending on media grouping setting
    this.addEventListener(
      EVENTS.PRODUCT_VARIANT_CHANGED,
      (event) => {
        if (event.detail.hasMediaGrouping) {
          this.handleRepaint(event);
        } else {
          this.updateActiveMainMediaToVariant(event);
        }
      },
      { signal: this.controller.signal },
    );

  document.addEventListener(
  EVENTS.SHOPIFY_SECTION_LOAD,
  () => this.handleLeftThumbnailContainerHeight(),
  { signal: this.controller.signal },
);

  }

  initActiveMedia() {
    this.media = this.querySelector(this.selectors.media);

    if (this.media === null) return

    this.activeMediaController = new AbortController();
    this.hasSingleMedia = this.dataset.hasSingleMedia === "true";

    this.mediaLayout = this.dataset.mediaLayout;
    this.mainMediaContainer = this.querySelector(
      this.selectors.mainMediaContainer,
    );
    this.initialMediaContainer = this.querySelector(
      this.selectors.initialMediaContainer,
    );

    // Setup media lightbox
    if (this.dataset.hasProductLightbox === "true") {
      setupMediaLightbox(this.mainMediaContainer);
    }

    // Setup model viewer
    setupModelViewer(this.mainMediaContainer);

    // Load video players
    this.videoPlayerContainers = this.querySelectorAll(
      this.selectors.videoPlayerContainer,
    );
    this.videoPlayerContainers.forEach((video) => liquidMadeVideoPlayer(video));

    //
    // Desktop carousel & all Mobile layouts
    //
    // Load thumbnail indicators
    if (this.mediaLayout === "thumbnails" && this.dataset.thumbnailPosition) {
      this.thumbnailPosition = this.dataset.thumbnailPosition;
    }

    if (!this.hasSingleMedia) {
this.activeThumb = this.querySelector(this.selectors.activeThumb);

// fallback: prima thumb visibile
if (!this.activeThumb) {
  this.activeThumb = this.querySelector(".product-thumbnails__item-link");
  this.activeThumb?.classList.add("active");
}

this.activeMediaId = this.activeThumb?.dataset?.thumbnailId || null;
this.scrollerId = this.dataset.scrollerId;



      // Listen for carousel sliding
      document.addEventListener(
        EVENTS.SCROLL_SLIDER_CHANGED(this.scrollerId),
        (event) => {
          const currentMediaEl = event.detail.currentElement;
          const previousMediaEl = event.detail.previousElement;
          this.mobileLoopButton = this.querySelector(
            this.selectors.mobileLoopButton,
          );

          if (currentMediaEl) {
            if (currentMediaEl !== this.mobileLoopButton) {
              this.updateActiveThumbnails(currentMediaEl.dataset.mediaItemId);
            }

            if (currentMediaEl.dataset.mediaType === "model") {
              this.initViewInYourSpace(currentMediaEl.dataset.mediaItemId);
            }
          }

          if (currentMediaEl) {
  this.setVisibleCurrentFromSliderEvent(currentMediaEl);
}


          // Pause & Add poster back to any active iframes
          if (this.mediaLayout === "thumbnails") {
            if (currentMediaEl) {
              this.updateCarouselA11yNav(currentMediaEl);
            }

            if (previousMediaEl) {
              const prevMediaType = previousMediaEl.dataset.mediaType;

              if (
                prevMediaType === "external_video" ||
                prevMediaType === "video"
              ) {
                const injectedVideoIframe = previousMediaEl.querySelector(
                  this.selectors.iframe,
                );
                const injectedVideoElement = previousMediaEl.querySelector(
                  this.selectors.video,
                );

                if (injectedVideoIframe || injectedVideoElement) {
                  pausePlayingVideos();
                }
              }
            }
          }
        },
        { signal: this.activeMediaController.signal },
      );

      //
      // Desktop carousel layouts only
      //
      if (this.mediaLayout === "thumbnails") {
        this.initThumbnailListeners();

        // Set tabindex within carousels
        this.querySelectorAll(this.selectors.carouselTabIndex).forEach((el) => {
          el.setAttribute("tabindex", "-1");
        });
      }
    }
  }
  /* ===========================
    VARIANT MEDIA FILTER (scoped)
     - stile "example": debounce + signature + idempotente
     - filtra per data-variant-ids (Liquid)
     - PhotoSwipe: abilita/disabilita trigger con .lightbox-media
  =========================== */

  getSelectedVariantIdFromDOM() {
    // prova a leggere l’input id del form più vicino (scoped, non globale)
    const root = this.closest("section") || document;
    const idInput =
      root.querySelector('form[action*="/cart/add"] input[name="id"]') ||
      root.querySelector('input[name="id"]');

    if (idInput?.value) return String(idInput.value);

    // fallback radio custom
    const checked = root.querySelector("[js-variant-input]:checked");
    if (checked?.value) return String(checked.value);

    // fallback select
    const sel = root.querySelector('select[name="id"]');
    if (sel?.value) return String(sel.value);

    return null;
  }

  splitIds(v) {
    return String(v || "")
      .split(",")
      .map((s) => s.trim())
      .filter(Boolean);
  }

getLightboxTriggers(wrapper) {
  const mediaRoot =
    wrapper.querySelector(".media__container") ||
    wrapper.querySelector(".media") ||
    wrapper;

  // ✅ trigger PhotoSwipe “veri” (in ordine di affidabilità)
  let triggers = Array.from(
    mediaRoot.querySelectorAll(
      [
        'a[data-pswp-src]',
        'a[data-pswp-width][data-pswp-height]',
        'a[href*="/cdn/shop/files/"]',
        'a[href$=".jpg"], a[href$=".jpeg"], a[href$=".png"], a[href$=".webp"], a[href$=".gif"]',
      ].join(",")
    )
  );

  // ✅ se trovi più link, tieni solo quelli che wrappano la media (evita link UI)
  if (triggers.length > 1) {
    triggers = triggers.filter(a =>
      a.querySelector("img, picture, video, model-viewer, svg")
    );
  }

  return triggers;
}




applyVariantMediaFilter(variantId) {
  const selected = String(variantId || "");
  if (!selected) return;

  // ✅ usa la cache completa (tutti i media), fallback se non esiste
  const allWrappers = Array.isArray(this._mediaNodesCache) && this._mediaNodesCache.length
    ? this._mediaNodesCache
    : Array.from(this.querySelectorAll("[js-media-item-wrapper]"));

const thumbs = Array.from(this.querySelectorAll("[js-product-thumbnail], .product-thumbnails__item, .product-thumbnails__item-link"));
  if (!allWrappers.length) return;

  // signature coerente sempre sul set completo
  const desired = allWrappers
    .map((w) => {
      const ids = this.splitIds(w.dataset.variantIds);
      const show = ids.includes(selected) || ids.includes("all");
      return show ? "1" : "0";
    })
    .join("");

  const signature = `${selected}|${desired}|${allWrappers.length}`;
  if (signature === this._lastFilterSignature) return;
  this._lastFilterSignature = signature;

  // aggiorna stati su TUTTI i wrapper (anche quelli non in DOM in questo momento)
  allWrappers.forEach((el) => {
    const ids = this.splitIds(el.dataset.variantIds);
    const show = ids.includes(selected) || ids.includes("all");

    el.hidden = !show;
    el.dataset.visible = show ? "1" : "0";
    el.classList.toggle("is-pswp-hidden", !show);

    const triggers = this.getLightboxTriggers(el);
    triggers.forEach((t) => {
      if (show) t.classList.add("lightbox-media");
      else t.classList.remove("lightbox-media");
    });
  });

  // thumbs
  thumbs.forEach((el) => {
    const ids = this.splitIds(el.dataset.variantIds);
    const show = ids.includes(selected) || ids.includes("all");
    el.hidden = !show;
    el.dataset.visible = show ? "1" : "0";
    el.classList.toggle("is-pswp-hidden", !show);
  });

  // 🔥 rebuild basato sulla cache completa
  this.rebuildVisibleMediaDOM(selected);

  // refresh slider
  this.querySelectorAll("scroll-slider").forEach((s) => s.refresh?.());
requestAnimationFrame(() => {
  this.querySelectorAll("scroll-slider").forEach((s) => s.refresh?.());
});

  // vai alla prima slide dopo rebuild
  if (this.scrollerId) {
    document.dispatchEvent(
      new CustomEvent(EVENTS.SCROLL_SLIDER_GO_TO_SLIDE(this.scrollerId), {
        detail: { slideIndex: 1, behavior: "instant" },
      })
    );
  }

  // riallinea active thumbnail alla prima slide visibile
const firstVisible = this.querySelector(".product-media__items [js-media-item-wrapper]");
if (firstVisible?.dataset?.mediaItemId) {
  this.updateActiveThumbnails(firstVisible.dataset.mediaItemId);
}


  // ✅ contatore coerente dopo rebuild
  this._visibleCurrentIndex = 1;
  this.updateVisibleCounter();

  // lightbox reinit (come già fai)
  if (this.dataset.hasProductLightbox === "true") {
    this.dispatchEvent(new CustomEvent("product:media:reinit", { detail: { fromFilter: true } }));
  }
}

  updateVisibleCounter() {
  const counter = this.querySelector("[js-media-counter]");
  if (!counter) return;

  const currentEl = counter.querySelector("[js-media-current]");
  const totalEl = counter.querySelector("[js-media-total]");
  if (!currentEl || !totalEl) return;

  const slides = Array.from(this.querySelectorAll(".product-media__items [js-media-item-wrapper]"));
  const total = slides.length || 1;

  // prova a leggere lo “slide attivo” dal tuo evento slider quando arriva
  // fallback: 1
  const current = this._visibleCurrentIndex || 1;

  currentEl.textContent = String(current);
  totalEl.textContent = String(total);
}

setVisibleCurrentFromSliderEvent(currentMediaEl) {
  const slides = Array.from(this.querySelectorAll(".product-media__items [js-media-item-wrapper]"));
  const idx = slides.findIndex((el) => el === currentMediaEl);
  this._visibleCurrentIndex = idx >= 0 ? idx + 1 : 1;
  this.updateVisibleCounter();
}


  scheduleVariantMediaFilter(variantId) {
    clearTimeout(this._filterTimer);
    this._filterTimer = setTimeout(() => {
      const id = variantId || this.getSelectedVariantIdFromDOM();
      if (id) this.applyVariantMediaFilter(id);
    }, 70);
  }
rebuildVisibleMediaDOM(selectedVariantId) {
  if (!this._mediaContainerEl || !this._mediaNodesCache?.length) return;

  const selected = String(selectedVariantId || "");
  const visibleNodes = this._mediaNodesCache.filter((el) => {
    const ids = this.splitIds(el.dataset.variantIds);
    return ids.includes(selected) || ids.includes("all");
  });

  // svuota e ri-appende solo le visibili
  this._mediaContainerEl.innerHTML = "";
  visibleNodes.forEach((n) => this._mediaContainerEl.appendChild(n));

  // ✅ riallinea indici sulle sole visibili
  this.reindexVisibleMedia();
}



  initVariantMediaFilter() {
    // stato interno
    this._lastFilterSignature = "";
    this._filterTimer = null;
// cache per rebuild DOM una sola volta
if (!this._mediaNodesCache) {
  this._mediaNodesCache = Array.from(this.querySelectorAll("[js-media-item-wrapper]"));
  this._mediaContainerEl = this.querySelector(".product-media__items");
}

    // run iniziale
    this.scheduleVariantMediaFilter(this.getSelectedVariantIdFromDOM());

    // quando cambia variante (evento tema)
    this.addEventListener(
      EVENTS.PRODUCT_VARIANT_CHANGED,
      (e) => {
        const id =
          String(e?.detail?.variant?.id || "") ||
          String(e?.detail?.newVariantId || "") ||
          this.getSelectedVariantIdFromDOM();

        this.scheduleVariantMediaFilter(id);
      },
      { signal: this.controller.signal }
    );

    // se qualcun altro forza reinit (ma evita loop)
    this.addEventListener(
      "product:media:reinit",
      (e) => {
        if (e?.detail?.fromFilter) return;
        this.scheduleVariantMediaFilter(this.getSelectedVariantIdFromDOM());
      },
      { signal: this.controller.signal }
    );

    // fallback change su radio/select dentro la sezione (scoped)
    const root = this.closest("section") || document;
    root.addEventListener(
      "change",
      (e) => {
        const t = e.target;
        if (!t) return;
        if (t.matches('input[type="radio"], select')) {
          this.scheduleVariantMediaFilter(this.getSelectedVariantIdFromDOM());
        }
      },
      { signal: this.controller.signal }
    );
  }

  handleLeftThumbnailContainerHeight() {
    if (!this.initialMediaContainer) return

    this.videoPoster = this.querySelector(this.selectors.videoPoster);
    const initialMedia = this.initialMediaContainer.querySelector(
      this.selectors.media,
    );
    const mediaEl = this.videoPoster ? this.videoPoster : initialMedia;

    // Allow for slight delay to improve container height within T.E.
    setTimeout(() => {
      this.style.setProperty(
        "--left-thumbnail-container-height",
        `${mediaEl.offsetHeight}px`,
      );
    }, 0);
  }

  initThumbnailListeners() {
    // Listen for page loaded to get left thumbnail column height
    if (this.thumbnailPosition === "left") {
      const initialImg = this.initialMediaContainer.querySelector(
        this.selectors.image,
      );

      initialImg
        .decode()
        .then(() => {
          this.handleLeftThumbnailContainerHeight();
        })
        .then(() => {
          initialImg.classList.add("initial-image");
        });
    }

    const smallDesktop = 1023; // 1023 to make sure 1024 is properly styled
    this.widthWatcher = srraf(({ vw, pvw }) => {
      if (vw !== pvw && vw > smallDesktop) {
        this.handleLeftThumbnailContainerHeight();
      }
    });

    // Listen for thumbnail container scroll to apply gradients
    this.thumbContainer = this.querySelector(this.selectors.thumbContainer);
    this.thumbGradient = this.querySelector(this.selectors.thumbGradient);

    this.thumbContainer.addEventListener(
      "scroll",
      () => {
        this.updateThumbGradientOpacity();
      },
      {
        signal: this.activeMediaController.signal,
      },
    );

    // Listen for thumbnail clicks
    this.thumbnailItems = this.querySelectorAll(this.selectors.thumb);
    this.thumbnailItems.forEach((item) => {
      item.addEventListener(
        "click",
        () => {
          this.updateActiveMainMedia(parseInt(item.dataset.thumbnailIndex));
          this.updateActiveThumbnails(item.dataset.thumbnailId);
        },
        { signal: this.activeMediaController.signal },
      );
    });
  }

  initViewInYourSpace(currentActiveMediaId) {
    this.querySelector(this.selectors.viewInYourSpace).setAttribute(
      "data-shopify-model3d-id",
      currentActiveMediaId,
    );
  }

  updateActiveMainMedia(currentThumbnailIndex) {
    const isDesktopViewport =
      (window.innerWidth || document.documentElement.clientWidth) > 1024;

    if (isDesktopViewport && this.mediaLayout === "columns") {
      const featuredMediaEl = this.querySelector(
        `${this.selectors.mediaById(currentThumbnailIndex)}`,
      );

      featuredMediaEl.scrollIntoView({
        behavior: "smooth",
        block: "center",
        inline: "nearest",
      });
    }

    // Scroll slider event included for Column layouts for mobile carousel coverage
    document.dispatchEvent(
      new CustomEvent(EVENTS.SCROLL_SLIDER_GO_TO_SLIDE(this.scrollerId), {
        detail: {
          slideIndex: currentThumbnailIndex,
          behavior: "instant",
        },
      }),
    );
  }

  updateActiveMainMediaToVariant(event) {
    const newMediaId = event.detail.newFeaturedMedia;
    this.mediaItemWrappers = this.querySelectorAll(
      this.selectors.mediaItemWrapper,
    );

    let newMediaIndex;
    for (const media of this.mediaItemWrappers) {
      if (media.dataset.mediaItemId === newMediaId) {
        newMediaIndex = parseInt(media.dataset.slideIndex);
        break
      }
    }

    this.updateActiveMainMedia(newMediaIndex);
  }

updateActiveThumbnails(currentActiveMediaId) {
  const lastActiveThumbnail = this.querySelector(this.selectors.activeThumb);

  let activeThumbnail = this.querySelector(
    `${this.selectors.thumbById(currentActiveMediaId)}`
  );

  if (!activeThumbnail) {
    activeThumbnail = this.querySelector(".product-thumbnails__item-link:not([hidden])")
      || this.querySelector(".product-thumbnails__item-link");
  }

  if (!activeThumbnail) return;

  // se non c’è un lastActive, imposta solo active
  if (!lastActiveThumbnail) {
    activeThumbnail.classList.add("active");
    this.activeMediaId = currentActiveMediaId;
    return;
  }

  if (activeThumbnail !== lastActiveThumbnail) {
    activeThumbnail.classList.add("active");
    lastActiveThumbnail.classList.remove("active");
    this.activeMediaId = currentActiveMediaId;

    if (this.mediaLayout === "thumbnails" && this.thumbContainer) {
      const scrollAttribute =
        this.thumbnailPosition === "left" ? "scrollTop" : "scrollLeft";

      const scrollDistance =
        this.thumbnailPosition === "left"
          ? activeThumbnail.offsetTop - activeThumbnail.getBoundingClientRect().height * 2
          : activeThumbnail.offsetLeft - activeThumbnail.getBoundingClientRect().width * 2;

      this.thumbContainer[scrollAttribute] = scrollDistance;
    }
  }
}


  updateThumbGradientOpacity() {
    let beforeOpacity = 0;
    let afterOpacity = 1;

    if (this.thumbnailPosition === "left") {
      if (this.thumbContainer.scrollTop !== 0) {
        beforeOpacity = 1;
      }

      const thumbContainerScrollHeight =
        this.thumbContainer.scrollHeight - this.thumbGradient.offsetHeight;
      const thumbContainerMaxScroll = this.thumbContainer.scrollTop + 1;
      if (thumbContainerMaxScroll >= thumbContainerScrollHeight) {
        afterOpacity = 0;
      }
    } else if (this.thumbnailPosition === "below") {
      if (this.thumbContainer.scrollLeft !== 0) {
        beforeOpacity = 1;
      }

      const thumbContainerScrollWidth =
        this.thumbContainer.scrollWidth - this.thumbGradient.offsetWidth;
      const thumbContainerMaxScroll = this.thumbContainer.scrollLeft + 1;

      if (thumbContainerMaxScroll >= thumbContainerScrollWidth) {
        afterOpacity = 0;
      }
    }

    this.thumbGradient.style.setProperty(
      "--thumb-opacity-before",
      beforeOpacity,
    );
    this.thumbGradient.style.setProperty("--thumb-opacity-after", afterOpacity);
  }

  updateCarouselA11yNav(currentActiveMediaEl) {
    this.allTabIndexEls = this.querySelectorAll(this.selectors.carouselTabIndex);
    this.currentTabIndexEls = currentActiveMediaEl.querySelectorAll(
      this.selectors.carouselTabIndex,
    );

    this.allTabIndexEls.forEach((el) => {
      el.setAttribute("tabindex", "-1");
    });

    this.currentTabIndexEls.forEach((el) => {
      el.removeAttribute("tabindex");
    });
  }

  handleRepaint(event) {
    // In the case that Media Grouping image order creates conflicts, prevent repaint
    if (event.detail.mediaSize > 0) {
      this.activeMediaController.abort();
      this.controller.abort();
      this.connectedCallback();
      this.initActiveMedia();
    }
  }

  disconnectedCallback() {
    this.controller.abort();
  }
}

if (!customElements.get("product-media")) {
  customElements.define("product-media", ProductMedia);
}


